import React, { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../App";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";
import { FiTarget, FiZap, FiCode, FiLayers, FiUsers, FiCheckCircle, FiTrendingUp, FiActivity } from "react-icons/fi";

// Asset Imports (Keeping your existing imports)
import banner1 from "../assets/banner1.jpg";
import banner2 from "../assets/banner2.jpg";

export default function AboutUs() {
  const appContext = useContext(AppContext);
  const images = [banner1, banner2]; // Simplified for demo
  const [activeImg, setActiveImg] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveImg((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <>
      <style>{`
        :root {
          --glass-bg: rgba(255, 255, 255, 0.03);
          --glass-border: rgba(255, 255, 255, 0.1);
        }

        .w-about-viewport {
          position: relative;
          width: 100%;
          min-height: 100vh;
          background: #0a1118;
          overflow: hidden;
          color: #fff;
          display: flex;
          align-items: center;
          font-family: 'Inter', system-ui, sans-serif;
        }

        .slider-bg { position: absolute; inset: 0; z-index: 0; }
        .slide {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transform: scale(1.1);
          transition: opacity 1.5s ease-in-out, transform 6s linear;
        }
        .slide.active { opacity: 0.25; transform: scale(1); }
        
        .slider-overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 20% 50%, rgba(10, 17, 24, 0.95) 0%, rgba(10, 17, 24, 0.7) 100%);
          z-index: 1;
        }

        .ab-hero-container {
          position: relative;
          z-index: 2;
          max-width: 1300px;
          margin: 0 auto;
          padding: 100px 20px;
        }

        .ab-tag {
          color: var(--primaryColor);
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 4px;
          font-size: 0.75rem;
          margin-bottom: 15px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ab-tag::before { content: ""; width: 20px; height: 2px; background: var(--primaryColor); }

        .ab-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 900;
          letter-spacing: -0.04em;
          margin-bottom: 30px;
          line-height: 1.1;
        }

        .ab-title span { color: var(--secondaryColor); opacity: 0.8; }

        /* ── Modern Performance Dashboard ── */
        .perf-dashboard {
          background: #fff;
          border-radius: 32px;
          padding: 40px;
          box-shadow: 0 40px 100px rgba(0,0,0,0.1);
          position: relative;
          overflow: hidden;
        }

        .perf-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 35px;
          border-bottom: 1px solid #f1f5f9;
          padding-bottom: 20px;
        }

        .bar-group { margin-bottom: 30px; }
        
        .bar-label-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          font-weight: 700;
          font-size: 0.85rem;
          color: #1e293b;
        }

        .modern-track {
          height: 12px;
          background: #f1f5f9;
          border-radius: 20px;
          position: relative;
          overflow: hidden;
        }

        .modern-fill {
          position: absolute;
          height: 100%;
          background: linear-gradient(90deg, var(--primaryColor), var(--secondaryColor));
          border-radius: 20px;
          width: 0;
          transition: width 2s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* ── Counter Cards ── */
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-top: 50px;
        }

        .stat-glass {
          background: var(--glass-bg);
          border: 1px solid var(--glass-border);
          backdrop-filter: blur(10px);
          padding: 30px;
          border-radius: 24px;
          transition: 0.3s;
        }

        .stat-glass:hover {
          border-color: var(--primaryColor);
          background: rgba(255,255,255,0.05);
        }

        @media (max-width: 992px) {
          .metrics-grid, .why-grid { grid-template-columns: 1fr; }
          .ab-hero-container { text-align: center; }
          .ab-tag { justify-content: center; }
        }
      `}</style>

      {/* Hero Section */}
      <section className="w-about-viewport">
        <div className="slider-bg">
          {images.map((img, i) => (
            <div key={i} className={`slide ${i === activeImg ? 'active' : ''}`} style={{ backgroundImage: `url(${img})` }} />
          ))}
        </div>
        <div className="slider-overlay" />

        <div className="ab-hero-container container">
          <div className="row align-items-center g-5">
            <div className="col-lg-7">
              <h1 className="ab-title">Engineering the <br/><span>Digital Future.</span></h1>
              <p className="lead mb-5" style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '600px' }}>
                We bridge the gap between complex challenges and elegant software solutions. 
                Our craft is defined by precision, scalability, and an obsession with user growth.
              </p>
              
              <div className="metrics-grid">
                <MiniStat icon={<FiLayers />} val="40+" label="Deployments" />
                <MiniStat icon={<FiUsers />} val="100k" label="Active Users" />
                <MiniStat icon={<FiCheckCircle />} val="30+" label="Partners" />
              </div>
            </div>

            <div className="col-lg-5">
               <div className="perf-dashboard">
                  <div className="perf-card-header">
                    <div>
                      <h5 className="mb-0 font-bold text-primary">Performance</h5>
                      <small className="text-muted">Live Optimization Metrics</small>
                    </div>
                    <FiActivity size={24} color="var(--primaryColor)" />
                  </div>

                  <LiveBar label="System Speed" percentage={98} />
                  <LiveBar label="Code Security" percentage={100} />
                  <LiveBar label="Operational Efficiency" percentage={95} />

                  <div className="mt-4 p-3 rounded-3" style={{background: '#f8fafc', borderLeft: '4px solid var(--secondaryColor)'}}>
                    <div className="d-flex align-items-center gap-3">
                      <FiTrendingUp color="var(--secondaryColor)" />
                      <span className="small fw-bold text-dark">Continuously Scaling for Excellence</span>
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections (Simplified for clean look) */}
      <section className="py-5 bg-white">
        <div className="container py-5">
          <div className="row g-5">
            {[
              { icon: <FiTarget />, title: "Vision", desc: "Pushing the boundaries of what's possible through infinite innovation." },
              { icon: <FiZap />, title: "Mission", desc: "Empowering global businesses with high-performance digital tools." },
              { icon: <FiCode />, title: "Motto", desc: "We Code, You Grow — our commitment to your bottom line." }
            ].map((item, idx) => (
              <div key={idx} className="col-md-4">
                <div className="p-5 h-100 transition-all hover-up" style={{background: '#f8fafc', borderRadius: '24px'}}>
                  <div className="mb-4 d-inline-block p-3 rounded-3" style={{background: 'rgba(var(--primaryColorRGB), 0.1)', color: 'var(--primaryColor)'}}>
                    {React.cloneElement(item.icon, { size: 32 })}
                  </div>
                  <h4 className="fw-900 mb-3" style={{color: 'var(--secondaryColor)'}}>{item.title}</h4>
                  <p className="text-muted mb-0">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function MiniStat({ icon, val, label }) {
  return (
    <div className="stat-glass">
      <div style={{color: 'var(--primaryColor)', marginBottom: '10px'}}>{icon}</div>
      <h3 className="mb-1 fw-900" style={{fontSize: '1.5rem'}}>{val}</h3>
      <span className="text-uppercase ls-1" style={{fontSize: '0.65rem', fontWeight: 800, opacity: 0.6}}>{label}</span>
    </div>
  );
}

function LiveBar({ label, percentage }) {
  const [width, setWidth] = useState(0);
  const barRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setWidth(percentage);
    }, { threshold: 0.1 });
    
    if (barRef.current) observer.observe(barRef.current);
    return () => observer.disconnect();
  }, [percentage]);

  return (
    <div className="bar-group" ref={barRef}>
      <div className="bar-label-row">
        <span>{label}</span>
        <span style={{color: 'var(--primaryColor)'}}>{width}%</span>
      </div>
      <div className="modern-track">
        <div className="modern-fill" style={{ width: `${width}%` }}>
          <div className="bar-glow" />
        </div>
      </div>
    </div>
  );
}